import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip } from '@mui/material';
import Swal from 'sweetalert2';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUILoadingLinear } from '../../../atomic/atoms';
import { PageUrl } from '../../../types/enums/pageUrl-enums';
import Helper from '../../../utils/helper';
import StoreManagerPermissionQueryService from '../../../services/query/storeManagerPermission/StoreManagerPermissionQuery.services';

interface HistoryRow {
    historyId: number;
    actorUsername: string;
    targetUsername: string;
    featureKey: string;
    action: string;
    storeCode: string;
    createdDate: string;
}

export const PermissionHistory: FC = () => {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState<HistoryRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const storeCode = Helper.getStoreCode();

    const mapHistoryItem = (item: any): HistoryRow => ({
        historyId: item.historyId ?? item.HistoryId,
        actorUsername: item.actorUsername ?? item.ActorUsername ?? '',
        targetUsername: item.targetUsername ?? item.TargetUsername ?? '',
        featureKey: item.featureKey ?? item.FeatureKey ?? '',
        action: item.action ?? item.Action ?? '',
        storeCode: item.storeCode ?? item.StoreCode ?? '',
        createdDate: item.createdDate ?? item.CreatedDate ?? '',
    });

    useEffect(() => {
        if (!storeCode) {
            setRows([]);
            setTotalCount(0);
            return;
        }

        const fetchHistory = async () => {
            setLoading(true);
            try {
                const response = await StoreManagerPermissionQueryService.GetHistory({
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                });

                const items = response?.items ?? response?.Items ?? (Array.isArray(response) ? response : []);
                setRows(Array.isArray(items) ? items.map(mapHistoryItem) : []);
                setTotalCount(response?.totalCount ?? response?.TotalCount ?? items?.length ?? 0);
            } catch {
                setRows([]);
                setTotalCount(0);
                Swal.fire({
                    title: t('general.error'),
                    text: t('storeManagerPermission.loadError'),
                    icon: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [pageNumber, pageSize, storeCode]);

    const formatAction = (action: string) => {
        if (action === 'Grant') {
            return t('storeManagerPermission.actionGrant');
        }
        if (action === 'Revoke') {
            return t('storeManagerPermission.actionRevoke');
        }
        return action || '-';
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'actorUsername',
            headerName: t('storeManagerPermission.actor'),
            flex: 1,
            minWidth: 140,
            sortable: false,
        },
        {
            field: 'targetUsername',
            headerName: t('storeManagerPermission.target'),
            flex: 1,
            minWidth: 140,
            sortable: false,
        },
        {
            field: 'featureKey',
            headerName: t('storeManagerPermission.feature'),
            flex: 1,
            minWidth: 120,
            sortable: false,
        },
        {
            field: 'action',
            headerName: t('storeManagerPermission.action'),
            width: 120,
            sortable: false,
            renderCell: (params) => {
                const chipColor = params.value === 'Grant' ? 'success' : 'default';
                return (
                    <Chip size="small" label={formatAction(params.value)} color={chipColor} variant="outlined" />
                );
            },
        },
        {
            field: 'storeCode',
            headerName: t('storeManagerPermission.store'),
            width: 120,
            sortable: false,
        },
        {
            field: 'createdDate',
            headerName: t('storeManagerPermission.date'),
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => {
                if (!params.value) {
                    return '-';
                }
                const date = new Date(params.value);
                return date.toLocaleString(i18n.language, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
        },
    ];

    return (
        <SCUIContent>
            <Box sx={{ mb: 2 }}>
                <SCUIBreadCrumb
                    title={t('storeManagerPermission.historyTitle')}
                    componentLink={PageUrl.storeManagerPermissionHistory.url}
                />
            </Box>
            <Box sx={{ position: 'relative', minHeight: '100%' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -24,
                        left: -24,
                        right: -24,
                        height: '4px',
                        zIndex: 9999,
                    }}
                >
                    <SCUILoadingLinear display={loading} />
                </Box>

                {!storeCode && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('storeManagerPermission.storeCodeMissing')}
                    </Alert>
                )}

                {storeCode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('storeManagerPermission.historyStoreScopeHint', { storeCode })}
                    </Alert>
                )}

                {storeCode && !loading && rows.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('storeManagerPermission.historyEmpty')}
                    </Alert>
                )}

                <SCUICustomDataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    getRowId={(row: HistoryRow) => row.historyId}
                    autoHeight
                    pagination
                    paginationModeChange={true}
                    rowCount={totalCount}
                    page={pageNumber - 1}
                    pageSize={pageSize}
                    onPageChange={(newPage: number) => setPageNumber(newPage + 1)}
                    onPageSizeChange={(newSize: number) => {
                        setPageSize(newSize);
                        setPageNumber(1);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>
        </SCUIContent>
    );
};
